import { createContext, useContext, useEffect, useState, ReactNode, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';

interface SocketContextType {
  socket: null;
  isConnected: boolean;
  onlineUsers: string[];
  emit: (event: string, data: unknown) => void;
  on: (event: string, callback: (data: unknown) => void) => void;
  off: (event: string, callback: (data: unknown) => void) => void;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

interface SocketProviderProps {
  children: ReactNode;
}

const isCloudflareDeployment = () => {
  const apiUrl = process.env.REACT_APP_API_URL || '';
  return apiUrl.includes('workers.dev') || 
         window.location.hostname.includes('vantax.co.za') ||
         window.location.hostname.includes('pages.dev');
};

export function SocketProvider({ children }: SocketProviderProps) {
  const [isConnected, setIsConnected] = useState(false);
  const [onlineUsers] = useState<string[]>([]);
  const { user, token } = useAuth();
  const eventListenersRef = useRef<Map<string, Set<(data: unknown) => void>>>(new Map());

  const emit = useCallback((event: string, data: unknown) => {
    if (!isCloudflareDeployment()) {
      console.log('Socket emit (polling mode):', event, data);
    }
  }, []);

  const on = useCallback((event: string, callback: (data: unknown) => void) => {
    if (!eventListenersRef.current.has(event)) {
      eventListenersRef.current.set(event, new Set());
    }
    eventListenersRef.current.get(event)?.add(callback);
  }, []);

  const off = useCallback((event: string, callback: (data: unknown) => void) => {
    eventListenersRef.current.get(event)?.delete(callback);
  }, []);

  // The online-users poll was removed: it hit the API from every open tab
  // every 30s and nothing read the result. `onlineUsers` stays in the context
  // shape (always empty) until a real presence feature needs it.
  useEffect(() => {
    if (!(user && token)) return;
    setIsConnected(true);
    return () => setIsConnected(false);
  }, [user, token]);

  const value: SocketContextType = {
    socket: null,
    isConnected,
    onlineUsers,
    emit,
    on,
    off,
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
}

export function useSocket() {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
}
